import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Plug, Upload, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtTRY } from "../../lib/format";

const inputCls = "h-10 px-3 bg-[#0F1115] border border-[#272B33] rounded-lg text-sm text-neutral-200 focus:outline-none focus:border-blue-500";
const SYNC_OPTS = [["products", "Ürünler"], ["prices", "Fiyatlar"], ["stock", "Stoklar"], ["images", "Görseller"], ["categories", "Kategoriler"]];

export default function SuppliersPage() {
  const [tab, setTab] = useState("genel");
  const [supplier, setSupplier] = useState(null);
  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [stateFilter, setStateFilter] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [opts, setOpts] = useState({ products: true, prices: true, stock: true, images: true, categories: true });
  const [kirtasiyeCats, setKirtasiyeCats] = useState([]);
  const [xmlStatus, setXmlStatus] = useState(null);
  const [xmlBusy, setXmlBusy] = useState(false);

  const loadXml = useCallback(async () => {
    const { data } = await api.get("/admin/suppliers/KADIOGLU/xml-status");
    setXmlStatus(data);
  }, []);

  const xmlAnalyze = async () => {
    setXmlBusy(true);
    try {
      const { data } = await api.post("/admin/suppliers/KADIOGLU/xml-analyze", {}, { timeout: 180000 });
      setXmlStatus(data);
      data.reachable ? toast.success(`XML analiz edildi: ${data.total_products} ürün, ${data.fields.length} alan`) : toast.error(data.error || "XML erişilemedi");
    } catch (e) { toast.error(errMsg(e)); } finally { setXmlBusy(false); }
  };

  const xmlImport = async () => {
    setXmlBusy(true);
    try {
      const { data } = await api.post("/admin/suppliers/KADIOGLU/xml-test-import", {}, { timeout: 180000 });
      toast.success(`${data.created} ürün taslak oluşturuldu, ${data.existing} zaten vardı`);
      loadXml(); loadProducts(); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setXmlBusy(false); }
  };

  const load = useCallback(async () => {
    const [{ data: sups }, { data: lg }] = await Promise.all([
      api.get("/admin/suppliers"), api.get("/admin/suppliers/KADIOGLU/logs")]);
    setSupplier(sups.find((s) => s.code === "KADIOGLU"));
    setLogs(lg);
    const { data: pr } = await api.get("/admin/suppliers/KADIOGLU/pricing");
    setPricing(pr);
    const { data: maps } = await api.get("/admin/suppliers/KADIOGLU/mappings");
    setMappings(maps);
    const { data: cats } = await api.get("/categories");
    const k = cats.find((c) => c.slug === "kirtasiye");
    setKirtasiyeCats(k?.children || []);
  }, []);

  const loadProducts = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "30" });
    if (stateFilter) params.set("state", stateFilter);
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/suppliers/KADIOGLU/products?${params}`);
    setProducts(data);
  }, [page, stateFilter, q]);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  useEffect(() => { loadProducts().catch((e) => toast.error(errMsg(e))); }, [loadProducts]);
  useEffect(() => { if (tab === "xml") loadXml().catch((e) => toast.error(errMsg(e))); }, [tab, loadXml]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/suppliers/KADIOGLU/sync", { options: opts });
      toast.success(`Senkron tamamlandı: ${d_total(data)} ürün, ${data.new} yeni, ${data.updated} güncellendi`);
      load(); loadProducts();
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };
  const d_total = (d) => d.total;

  const runTest = async (forceLive = false) => {
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/suppliers/KADIOGLU/test${forceLive ? "?force_live=true" : ""}`);
      data.ok ? toast.success(data.detail) : toast.error(data.detail, { duration: 8000 });
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  const publish = async (spId, pub) => {
    try {
      await api.post(`/admin/suppliers/products/${spId}/${pub ? "publish" : "unpublish"}`);
      toast.success(pub ? "Ürün yayınlandı" : "Ürün yayından alındı");
      loadProducts(); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remap = async (spId, categoryId) => {
    try {
      await api.put(`/admin/suppliers/products/${spId}/mapping`, { category_id: categoryId || null });
      toast.success("Kategori eşleşmesi güncellendi");
      loadProducts();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remapRule = async (mapId, categoryId) => {
    try {
      const { data } = await api.put(`/admin/suppliers/mappings/${mapId}`, { category_id: categoryId || null });
      toast.success(`Kural güncellendi, ${data.affected_products} ürün etkilendi`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePricing = async () => {
    try {
      await api.put("/admin/suppliers/KADIOGLU/pricing", pricing);
      toast.success("Fiyat kuralları kaydedildi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const catName = (id) => kirtasiyeCats.find((c) => c.category_id === id)?.name || "—";

  const TABS = [["genel", "Genel & Senkron"], ["urunler", `Ürünler${supplier ? ` (${supplier.draft_count} taslak)` : ""}`], ["mapping", "Kategori Eşleştirme"], ["fiyat", "Fiyat Kuralları"], ["xml", "XML Entegrasyonu"]];

  return (
    <div className="space-y-5" data-testid="admin-suppliers">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Tedarikçi Entegrasyonları</h1>
        <div className="flex gap-1 bg-[#181B20] border border-[#272B33] rounded-xl p-1">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3.5 h-9 rounded-lg text-xs font-semibold transition-colors ${tab === k ? "bg-blue-600 text-white" : "text-neutral-400 hover:text-white"}`}
              data-testid={`suppliers-tab-${k}`}>{label}</button>
          ))}
        </div>
      </div>

      {supplier?.server_ip && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl px-5 py-4 flex items-start gap-3" data-testid="supplier-whitelist-ip-banner">
          <Plug size={16} className="text-blue-400 mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            <p className="text-blue-300 font-semibold">Whitelist (IP İzin) Bilgisi — Sunucu IP: <span className="font-mono-ed text-white">{supplier.server_ip}</span></p>
            <p className="text-neutral-400 mt-1">Tedarikçi panellerinde (Kadıoğlu, Yeni Nesil, Bilgisayarım vb.) XML erişimi için bu IP adresine izin verin. Otomatik stok/fiyat senkronizasyonu bu IP üzerinden çalışır.</p>
          </div>
        </div>
      )}

      {tab === "genel" && supplier && (
        <div className="space-y-5">
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-white font-bold text-lg">{supplier.name} <span className="text-[11px] font-mono-ed text-neutral-500 ml-2">{supplier.code}</span></p>
                <p className="text-xs text-neutral-500 mt-1">{supplier.ws_url}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${supplier.has_credentials ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`} data-testid="supplier-cred-badge">
                    Kimlik: {supplier.has_credentials ? "Tanımlı (env)" : "EKSİK"}
                  </span>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-400" data-testid="supplier-mode-badge">Mod: {supplier.mode}</span>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${supplier.auto_sync_enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-500/15 text-neutral-400"}`}>
                    Otomatik senkron: {supplier.auto_sync_enabled ? "Açık" : "Kapalı"}
                  </span>
                  <span className="text-[11px] text-neutral-500 py-1">{supplier.product_count} ürün · son senkron: {supplier.last_sync_at ? new Date(supplier.last_sync_at).toLocaleString("tr-TR") : "—"}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => runTest(false)} disabled={testing} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-300 hover:border-blue-500 flex items-center gap-2 disabled:opacity-50" data-testid="supplier-test-btn">
                  <Plug size={15} /> {testing ? "Test ediliyor..." : "Bağlantıyı Test Et"}
                </button>
                <button onClick={() => runTest(true)} disabled={testing} className="h-10 px-4 rounded-xl border border-amber-600/40 text-sm text-amber-400 hover:border-amber-500 flex items-center gap-2 disabled:opacity-50" data-testid="supplier-test-live-btn">
                  <Plug size={15} /> Canlı Kimlik Testi
                </button>
              </div>
            </div>
          </div>

          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-white font-semibold mb-3">Manuel Senkronizasyon</p>
            <div className="flex items-center gap-4 flex-wrap mb-4">
              {SYNC_OPTS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                  <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="accent-blue-500 w-4 h-4" data-testid={`sync-opt-${k}`} /> {label}
                </label>
              ))}
            </div>
            <button onClick={runSync} disabled={syncing}
              className="h-11 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 flex items-center gap-2 disabled:opacity-50"
              data-testid="sync-now-btn">
              <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Senkronize ediliyor..." : "Şimdi Senkronize Et"}
            </button>
            <p className="text-[11px] text-neutral-500 mt-3">Not: Tam katalog senkronu onayınızdan sonra yapılacaktır. Otomatik senkron altyapısı hazır, kapalı tutuluyor.</p>
          </div>

          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
            <p className="text-white font-semibold px-5 pt-4 pb-3">Senkronizasyon Logları</p>
            {logs.length === 0 && <p className="text-neutral-500 text-sm px-5 pb-4">Henüz log yok</p>}
            {logs.map((l) => (
              <div key={l.log_id} className="px-5 py-3 border-t border-[#272B33]/50 text-xs flex items-center gap-4 flex-wrap" data-testid={`sync-log-${l.log_id}`}>
                {l.status === "DONE" ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-rose-400" />}
                <span className="text-neutral-400 font-mono-ed">{new Date(l.started_at).toLocaleString("tr-TR")}</span>
                <span className="text-neutral-300">{l.total} ürün · {l.new} yeni · {l.updated} güncellendi · {l.deactivated} pasif</span>
                <span className="text-neutral-500">stok {l.stock_changes} · fiyat {l.price_changes} · görsel {l.image_changes} · hata {l.errors?.length || 0}</span>
                {l.errors?.length > 0 && <span className="text-rose-400">{l.errors[0].message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "urunler" && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Ürün, kod veya barkod ara..." className={`${inputCls} flex-1 min-w-52`} data-testid="supplier-product-search" />
            <select value={stateFilter} onChange={(e) => { setStateFilter(e.target.value); setPage(1); }} className={inputCls} data-testid="supplier-state-filter">
              <option value="">Tümü</option><option value="draft">Taslak</option><option value="published">Yayında</option>
            </select>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="text-left text-[11px] text-neutral-500 border-b border-[#272B33]">
                <th className="px-4 py-3">Ürün</th><th className="px-4 py-3">Kod / Barkod</th><th className="px-4 py-3">Maliyet</th><th className="px-4 py-3">Satış (kurallı)</th><th className="px-4 py-3">Stok</th><th className="px-4 py-3">Kategori</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {products?.items?.map((p) => (
                  <tr key={p.sp_id} className="border-b border-[#272B33]/50 hover:bg-white/[0.02]" data-testid={`sp-row-${p.sp_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.local_image_url ? <img src={p.local_image_url} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-[#0F1115]" />}
                        <div>
                          <p className="text-neutral-100 font-medium leading-tight">{p.name}</p>
                          <p className="text-[10px] text-neutral-500">{p.brand} · {p.supplier_category_path}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-400 font-mono-ed">{p.supplier_product_code}<br />{p.barcode}</td>
                    <td className="px-4 py-3 text-neutral-300">
                      {fmtTRY(p.cost_price)}
                      <p className="text-[10px] text-neutral-500">KDV hariç · KDV %{p.vat_rate ?? 20}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white font-semibold">{fmtTRY(p.price_inc_vat ?? p.selling_price)}</span>
                      <p className="text-[10px] text-neutral-500">%{p.profit_rate ?? "—"} kâr · {fmtTRY(p.price_ex_vat ?? 0)} + KDV</p>
                    </td>
                    <td className="px-4 py-3"><span className={p.supplier_stock > 0 ? "text-emerald-400" : "text-rose-400"}>{p.supplier_stock}</span></td>
                    <td className="px-4 py-3">
                      <select value={p.mapped_category_id || ""} onChange={(e) => remap(p.sp_id, e.target.value)} className="h-8 px-2 bg-[#0F1115] border border-[#272B33] rounded-lg text-xs text-neutral-300 max-w-44" data-testid={`sp-mapping-${p.sp_id}`}>
                        <option value="">—</option>
                        {kirtasiyeCats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                      </select>
                      {p.mapping_source === "MANUAL" && <span className="text-[9px] text-blue-400 ml-1">MANUEL</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => publish(p.sp_id, !p.published)}
                        className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${p.published ? "bg-neutral-700/40 text-neutral-300 hover:bg-rose-600/20 hover:text-rose-300" : "bg-emerald-600 text-white hover:bg-emerald-500"}`}
                        data-testid={`sp-publish-${p.sp_id}`}>
                        {p.published ? <><EyeOff size={13} /> Yayından Al</> : <><Eye size={13} /> Yayınla</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products && !products.items.length && <p className="text-center text-neutral-500 text-sm py-10">Kayıt yok</p>}
          </div>
          {products?.pages > 1 && (
            <div className="flex justify-center gap-2">
              {Array.from({ length: products.pages }).map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} className={`w-9 h-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-blue-600 text-white" : "bg-[#181B20] border border-[#272B33] text-neutral-400"}`} data-testid={`sp-page-${i + 1}`}>{i + 1}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "mapping" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
          <p className="text-white font-semibold px-5 pt-4 pb-3">Kategori Eşleştirme (Kırtasiye ağacı)</p>
          {mappings.length === 0 && <p className="text-neutral-500 text-sm px-5 pb-4">Senkron sonrası tedarikçi kategorileri burada listelenir</p>}
          {mappings.map((m) => (
            <div key={m.map_id} className="px-5 py-3 border-t border-[#272B33]/50 flex items-center justify-between gap-4 flex-wrap" data-testid={`mapping-row-${m.map_id}`}>
              <div className="min-w-0">
                <p className="text-sm text-neutral-200 truncate">{m.supplier_path || "(kategorisiz)"}</p>
                <p className="text-[11px] text-neutral-500">{m.product_count} ürün · {m.sample_product}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${m.source === "MANUAL" ? "bg-blue-500/15 text-blue-400" : "bg-neutral-500/15 text-neutral-400"}`}>{m.source}</span>
                <select value={m.category_id || ""} onChange={(e) => remapRule(m.map_id, e.target.value)} className="h-9 px-2 bg-[#0F1115] border border-[#272B33] rounded-lg text-xs text-neutral-300 max-w-52" data-testid={`mapping-select-${m.map_id}`}>
                  <option value="">—</option>
                  {kirtasiyeCats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "xml" && (
        <div className="space-y-5" data-testid="xml-tab">
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-white font-semibold">Kadıoğlu XML Katalog</p>
                <p className="text-xs text-neutral-500 mt-1">https://www.kirtasiyem.com.tr/content/xml/urunler.xml (kimlik gerektirmez)</p>
                <div className="flex gap-2 mt-3 flex-wrap items-center">
                  {xmlStatus && (
                    <>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${xmlStatus.reachable ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`} data-testid="xml-reachable-badge">
                        Bağlantı: {xmlStatus.reachable === null ? "Henüz test edilmedi" : xmlStatus.reachable ? "AÇIK" : "HATA"}
                      </span>
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-400" data-testid="xml-total-badge">
                        {xmlStatus.total_products?.toLocaleString("tr-TR")} ürün
                      </span>
                      <span className="text-[11px] text-neutral-500">son kontrol: {xmlStatus.checked_at ? new Date(xmlStatus.checked_at).toLocaleString("tr-TR") : "—"}</span>
                    </>
                  )}
                </div>
                {xmlStatus?.error && <p className="text-xs text-rose-400 mt-2" data-testid="xml-error">{xmlStatus.error}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={xmlAnalyze} disabled={xmlBusy} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-300 hover:border-blue-500 flex items-center gap-2 disabled:opacity-50" data-testid="xml-analyze-btn">
                  <RefreshCw size={14} className={xmlBusy ? "animate-spin" : ""} /> XML'i Analiz Et
                </button>
                <button onClick={xmlImport} disabled={xmlBusy || !xmlStatus?.reachable} className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-50" data-testid="xml-import-btn">
                  5 Test Ürününü Taslak Oluştur
                </button>
              </div>
            </div>
          </div>

          {xmlStatus?.field_status && Object.keys(xmlStatus.field_status).length > 0 && (
            <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
              <p className="text-white font-semibold mb-3">Alan Durumları</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(xmlStatus.field_status).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 text-xs" data-testid={`xml-field-status-${k}`}>
                    <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${v.startsWith("OK") ? "bg-emerald-400" : v.startsWith("KISMI") ? "bg-amber-400" : "bg-rose-400"}`} />
                    <div><span className="text-neutral-300 font-semibold uppercase">{k}:</span> <span className="text-neutral-400">{v}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {xmlStatus?.first5?.length > 0 && (
            <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
              <p className="text-white font-semibold px-5 pt-4 pb-3">İlk 5 Test Ürünü (XML'den okunan ham veri)</p>
              {xmlStatus.first5.map((p, i) => (
                <div key={p.supplier_product_id} className="px-5 py-3 border-t border-[#272B33]/50" data-testid={`xml-product-${i}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm text-neutral-100 font-medium">{i + 1}. {p.name}</p>
                      <p className="text-[11px] text-neutral-500 font-mono-ed mt-0.5">
                        kod {p.supplier_product_code} · barkod {p.barcode || "—"} · {p.brand} · KDV %{p.vat_rate} · stok {p.supplier_stock} {p.unit}
                      </p>
                      <p className="text-[11px] text-neutral-500">{p.supplier_category_path}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-neutral-400">maliyet: {fmtTRY(p.cost_price)}</p>
                      {p.selling_price != null && <p className="text-xs text-emerald-400">satış (kurallı): {fmtTRY(p.selling_price)}</p>}
                      {p.imported !== undefined && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.imported ? (p.published ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400") : "bg-neutral-500/15 text-neutral-400"}`}>
                          {p.imported ? (p.published ? "YAYINDA" : "TASLAK") : "AKTARILMADI"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {xmlStatus?.fields?.length > 0 && (
            <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
              <p className="text-white font-semibold px-5 pt-4 pb-3">Alan Eşleştirme Sonucu ({xmlStatus.fields.length} alan)</p>
              <div className="max-h-80 overflow-y-auto">
                {xmlStatus.fields.map((f) => (
                  <div key={f.field} className="px-5 py-2 border-t border-[#272B33]/50 flex items-center justify-between gap-3 text-xs">
                    <span className="text-neutral-200 font-mono-ed">{f.field}</span>
                    <span className="text-neutral-500 truncate flex-1 text-right">{f.sample?.slice(0, 40)}</span>
                    <span className={`font-semibold shrink-0 ${f.pct >= 90 ? "text-emerald-400" : f.pct >= 50 ? "text-amber-400" : "text-neutral-500"}`}>%{f.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "fiyat" && pricing && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 space-y-4" data-testid="pricing-editor">
          <div className="flex items-center justify-between">
            <p className="text-white font-semibold">Fiyatlandırma Kuralları (maliyet aralığına göre)</p>
            <label className="flex items-center gap-2 text-sm text-neutral-300">
              <input type="checkbox" checked={pricing.active} onChange={(e) => setPricing({ ...pricing, active: e.target.checked })} className="accent-blue-500 w-4 h-4" data-testid="pricing-active" /> Kurallar aktif
            </label>
          </div>
          <div className="space-y-2">
            {pricing.tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-center" data-testid={`pricing-tier-${i}`}>
                <input type="number" value={t.min} onChange={(e) => { const tiers = [...pricing.tiers]; tiers[i] = { ...t, min: +e.target.value }; setPricing({ ...pricing, tiers }); }} className={inputCls} placeholder="Min ₺" data-testid={`tier-min-${i}`} />
                <input type="number" value={t.max ?? ""} onChange={(e) => { const tiers = [...pricing.tiers]; tiers[i] = { ...t, max: e.target.value === "" ? null : +e.target.value }; setPricing({ ...pricing, tiers }); }} className={inputCls} placeholder="Maks ₺ (boş=∞)" data-testid={`tier-max-${i}`} />
                <input type="number" value={t.margin_percent} onChange={(e) => { const tiers = [...pricing.tiers]; tiers[i] = { ...t, margin_percent: +e.target.value }; setPricing({ ...pricing, tiers }); }} className={inputCls} placeholder="Kâr %" data-testid={`tier-margin-${i}`} />
                <input type="number" value={t.fixed_amount || 0} onChange={(e) => { const tiers = [...pricing.tiers]; tiers[i] = { ...t, fixed_amount: +e.target.value }; setPricing({ ...pricing, tiers }); }} className={inputCls} placeholder="Sabit ₺" data-testid={`tier-fixed-${i}`} />
                <input type="number" value={t.min_profit || 0} onChange={(e) => { const tiers = [...pricing.tiers]; tiers[i] = { ...t, min_profit: +e.target.value }; setPricing({ ...pricing, tiers }); }} className={inputCls} placeholder="Min kâr ₺" data-testid={`tier-minprofit-${i}`} />
                <button onClick={() => setPricing({ ...pricing, tiers: pricing.tiers.filter((_, j) => j !== i) })} className="text-rose-400 text-xs hover:underline" data-testid={`tier-delete-${i}`}>Sil</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPricing({ ...pricing, tiers: [...pricing.tiers, { min: 0, max: null, margin_percent: 25, fixed_amount: 0, min_profit: 0 }] })} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-300 hover:border-blue-500" data-testid="pricing-add-tier">+ Kademe Ekle</button>
            <button onClick={savePricing} className="h-10 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 flex items-center gap-2" data-testid="pricing-save"><Upload size={14} /> Kaydet</button>
          </div>
          <p className="text-[11px] text-neutral-500">Sabit ₺ doluysa kâr % yerine uygulanır. Min kâr doluysa satış fiyatı en az (maliyet + min kâr) olur. Kaydettikten sonra bir sonraki senkronda fiyatlar yeniden hesaplanır.</p>
        </div>
      )}
    </div>
  );
}
